import { costModel } from '../data/pricing';
import type { WarrantyTier } from '../data/pricing';

export type MarginResult =
  | { status: 'unconfirmed'; missing: string[] }
  | {
      status: 'ok';
      revenue: number;
      productCost: number;
      packaging: number;
      shipping: number;
      processing: number;
      warranty: number;
      contribution: number;
      contributionPct: number;
    };

const round = (n: number) => Math.round(n * 100) / 100;

/**
 * Contribution margin for an offer. `incremental` = add-on lights shipped
 * with an existing order (no per-order packaging/shipping/fixed fee).
 * Returns 'unconfirmed' with the missing inputs when any cost is unknown.
 */
export function contributionMargin(
  revenue: number,
  units: number,
  tier: WarrantyTier,
  incremental = false,
): MarginResult {
  const c = costModel;
  const required: [string, number | null][] = [
    ['unitCost', c.unitCost],
    ['packagingCostPerUnit', c.packagingCostPerUnit],
    ['shippingCostPerAdditionalUnit', c.shippingCostPerAdditionalUnit],
    ['paymentProcessingPct', c.paymentProcessingPct],
    [`warrantyAllowancePct.${tier}`, c.warrantyAllowancePct[tier]],
  ];
  if (!incremental) {
    required.push(
      ['packagingCostPerOrder', c.packagingCostPerOrder],
      ['shippingCostPerOrder', c.shippingCostPerOrder],
      ['paymentProcessingFixed', c.paymentProcessingFixed],
    );
  }
  const missing = required.filter(([, v]) => v === null).map(([k]) => k);
  if (missing.length) return { status: 'unconfirmed', missing };

  const n = (v: number | null) => v as number;
  const productCost = units * n(c.unitCost);
  const packaging = (incremental ? 0 : n(c.packagingCostPerOrder)) + units * n(c.packagingCostPerUnit);
  const shipping = incremental
    ? units * n(c.shippingCostPerAdditionalUnit)
    : n(c.shippingCostPerOrder) + Math.max(0, units - 1) * n(c.shippingCostPerAdditionalUnit);
  const processing = (revenue * n(c.paymentProcessingPct)) / 100 + (incremental ? 0 : n(c.paymentProcessingFixed));
  const warranty = (productCost * n(c.warrantyAllowancePct[tier])) / 100;
  const contribution = revenue - productCost - packaging - shipping - processing - warranty;

  return {
    status: 'ok',
    revenue: round(revenue),
    productCost: round(productCost),
    packaging: round(packaging),
    shipping: round(shipping),
    processing: round(processing),
    warranty: round(warranty),
    contribution: round(contribution),
    contributionPct: Math.round((contribution / revenue) * 1000) / 10,
  };
}
